import { getPageInformation } from "@/app/api/admin";
import {
  getCandidateScreenings,
  getScreeningsByLocation,
} from "@/app/api/screeners";
import type { DataDumpResponse } from "@/types/api";
import { ExtensionAcceptedMessageTypes } from "@/types/extension";
import type { Nullable } from "@/types/utils";

// DEVOPS2-571: the admin app only has an extension parent when the legacy
// Chrome extension iframes it into an ATS page. Resolve that parent's actual
// origin so messages can never be exchanged with an unexpected window — the
// previous wildcard ("*") target would hand page metadata to ANY embedding
// page. frame-ancestors (DEVOPS2-567) bounds who can frame us; this bounds
// both where outbound messages go and (via ExtensionProvider) whose inbound
// messages we trust. Fail closed: no provable parent origin, no messaging.
export const getExtensionParentOrigin = (): Nullable<string> => {
  if (window.parent === window) {
    return null; // not framed — there is no extension to talk to
  }

  // Chromium exposes the ancestor chain; the extension is Chrome-only, so
  // this is the primary path. [0] is the immediate parent (the ATS page).
  const ancestorOrigin = window.location.ancestorOrigins?.[0];
  if (ancestorOrigin && ancestorOrigin !== "null") {
    return ancestorOrigin;
  }

  // Fallback when ancestorOrigins is unavailable: the embedder's origin via
  // referrer (strict-origin-when-cross-origin still sends the bare origin).
  // Practically unreachable for this Chrome-only extension — ancestorOrigins
  // is the trust anchor; never lean on this branch as one.
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }

  return null;
};

const postMessageToExtension = <
  T extends { type: ExtensionAcceptedMessageTypes },
>(
  body: T,
) => {
  const targetOrigin = getExtensionParentOrigin();

  if (!targetOrigin) {
    return;
  }

  parent.postMessage(body, targetOrigin);
};

const initializeAdmin = async ({ atsDomain }: { atsDomain: string }) => {
  postMessageToExtension({
    atsDomain: atsDomain,
    type: ExtensionAcceptedMessageTypes.AdminInitialized,
  });
};

const sendJobPageData = async (pageInformation: DataDumpResponse) => {
  const screenerLocationId = pageInformation.screenerLocationIds[0];

  if (!screenerLocationId) {
    postMessageToExtension({
      iconText: null,
      pageWidth: "full",
      type: ExtensionAcceptedMessageTypes.AdminStateChange,
    });
    return;
  }

  const screenerLocation = await getScreeningsByLocation(screenerLocationId);

  postMessageToExtension({
    iconText: `View ${screenerLocation.screenerSessions.length} screened candidates for this position!`,
    pageWidth: "full",
    type: ExtensionAcceptedMessageTypes.AdminStateChange,
  });
};

const sendCandidatePageData = async (pageInformation: DataDumpResponse) => {
  const { candidateEmails } = pageInformation;
  const candidateEmail = candidateEmails[0];
  const candidate = await getCandidateScreenings(candidateEmail);
  postMessageToExtension({
    iconText: `View ${candidate.screenerSessions.length} screeners for this candidate!`,
    pageWidth: "candidate",
    type: ExtensionAcceptedMessageTypes.AdminStateChange,
  });
};

// send dom to backend to get page information
const sendPageData = async ({
  companyId,
  dom,
}: {
  companyId: string;
  dom: string;
}) => {
  const pageInformation = await getPageInformation(companyId, dom);

  if (pageInformation.page === "job") {
    sendJobPageData(pageInformation);
  } else if (pageInformation.page === "candidate") {
    sendCandidatePageData(pageInformation);
  } else {
    postMessageToExtension({
      iconText: null,
      pageWidth: "full",
      type: ExtensionAcceptedMessageTypes.AdminStateChange,
    });
  }
  return pageInformation;
};

const closeExtension = () => {
  postMessageToExtension({
    type: ExtensionAcceptedMessageTypes.SetExtensionOpenState,
    open: false,
  });
};

export { initializeAdmin, sendPageData, closeExtension };
